import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchApplicationSuites } from 'common/services/api/ApplicationSuites/ApplicationSuites.thunks';
import { fetchUserProfileTenant } from 'common/services/api/UserProfileGlobalRoles/UserProfileGlobalRoles.thunk';
import { ApplicationSuite, AppSuiteTenant } from 'common/types/Entities';

import { IUserAppSuiteAndTenants } from './UserProfileApps';

interface UserProfileAppsSliceState {
    appSuites: ApplicationSuite[];
    tenants: AppSuiteTenant[];
    userProfileAppsTenants: IUserAppSuiteAndTenants[];
    processing: boolean;
}

type StateWithUserProfileAppsTenants = { userProfileAppsTenants: UserProfileAppsSliceState };

const initialState: UserProfileAppsSliceState = {
    appSuites: [],
    tenants: [],
    userProfileAppsTenants: [],
    processing: true,
};

const userProfileAppsSlice = createSlice({
    name: 'userProfileApps',
    initialState,

    reducers: (create) => ({
        setAppTenants: create.reducer((state, action: PayloadAction<AppSuiteTenant[]>) => {
            state.tenants = action.payload;
        }),

        clearAppTenants: create.reducer(state => {
            state.tenants = [];
        }),

        setUserProfileAppsTenants: create.reducer((state, action: PayloadAction<IUserAppSuiteAndTenants[]>) => {
            state.userProfileAppsTenants = action.payload;
        }),

        setProcessing: create.reducer((state, action: PayloadAction<boolean>) => {
            state.processing = action.payload;
        })
    }),

    extraReducers: builder => {
        builder.addCase(fetchApplicationSuites.pending, state => {
            state.appSuites = [];
        });
        builder.addCase(fetchApplicationSuites.fulfilled, (state, action: PayloadAction<ApplicationSuite[]>) => {
            state.appSuites = action.payload;
        });
        builder.addCase(fetchApplicationSuites.rejected, state => {
            state.appSuites = [];
        });
        builder.addCase(fetchUserProfileTenant.fulfilled, (state, action: PayloadAction<{
            tenantId: string,
            isActive: boolean,
            notes?: string,
            startDate?: string,
            endDate?: string,
            status?: string,
            aclCounts?: number,
            roleCounts?: number,
            claimCounts?: number,
            globalRoleCounts?: number
        }>) => {
            const updatingTenant = state.userProfileAppsTenants.flatMap(appSuite => appSuite.tenants).find(tenant => tenant.id === action.payload.tenantId);

            if (updatingTenant) {
                updatingTenant.isActive = action.payload.isActive;
                updatingTenant.notes = action.payload.notes;
                updatingTenant.startDate = action.payload.startDate;
                updatingTenant.endDate = action.payload.endDate;
                updatingTenant.status = action.payload.status;
                updatingTenant.aclCounts = action.payload.aclCounts;
                updatingTenant.roleCounts = action.payload.roleCounts;
                updatingTenant.claimCounts = action.payload.claimCounts;
                updatingTenant.globalRoleCounts = action.payload.globalRoleCounts;
            }
        });
    }
});

// eslint-disable-next-line no-restricted-syntax
export default userProfileAppsSlice.reducer;
export const { setProcessing, setUserProfileAppsTenants, setAppTenants, clearAppTenants } = userProfileAppsSlice.actions;
export const selectAppSuites = (state: StateWithUserProfileAppsTenants) => state.userProfileAppsTenants.appSuites;
export const selectTenants = (state: StateWithUserProfileAppsTenants) => state.userProfileAppsTenants.tenants;
export const selectUserProfileAppsTenants = (state: StateWithUserProfileAppsTenants) => state.userProfileAppsTenants.userProfileAppsTenants;
export const selectProcessing = (state: StateWithUserProfileAppsTenants) => state.userProfileAppsTenants.processing;
